import { CanonicalPresentationModelV1Schema, } from "../contracts/canonical-presentation.js";
import { stableStringify } from "../hashing/canonical-json.js";
const MISSING_BUSINESS_NAME_FALLBACK = "This client";
const READY_COPY = {
    journeyStage: "Review business information",
    title: "Confirm the client's business information",
    reason: "The client submitted their information. Review it before building the next website preview.",
    primaryLabel: "Review business information",
    progressLabel: "Stage 3 of 7",
    expectedResult: "Confirmed information becomes ready for the website build step.",
};
function resolveDisplayName(input) {
    const fromBusiness = input.businessName?.trim();
    if (fromBusiness)
        return fromBusiness;
    const fromClient = input.clientDisplayName?.trim();
    if (fromClient)
        return fromClient;
    return MISSING_BUSINESS_NAME_FALLBACK;
}
function idsAgree(input) {
    const projectId = input.projectId.trim();
    if (!projectId)
        return false;
    if (!input.clientReview || !input.lifecycle)
        return false;
    if (input.clientReview.projectId !== projectId)
        return false;
    if (input.lifecycle.projectId !== projectId)
        return false;
    return true;
}
/**
 * Fail-closed Agency Today Start Here adapter (ENR-UX-006).
 * Returns READY only for the exact authorized intake-submitted review state.
 * Never calculates eligibility, approval validity, or lifecycle transitions.
 */
export function deriveAgencyTodayStartHere(input) {
    if (!idsAgree(input))
        return null;
    const review = input.clientReview;
    const lifecycle = input.lifecycle;
    if (review.clientLink?.status !== "INTAKE_SUBMITTED")
        return null;
    if (lifecycle.simplePhase !== "review_and_build")
        return null;
    if (lifecycle.primaryActionId !== "confirm-build")
        return null;
    if (lifecycle.flags.qaFailure)
        return null;
    if (lifecycle.flags.generating)
        return null;
    if (lifecycle.flags.packaging)
        return null;
    if (lifecycle.flags.generationLock)
        return null;
    const displayName = resolveDisplayName(input);
    const projectId = input.projectId.trim();
    return CanonicalPresentationModelV1Schema.parse({
        schemaId: "enrein.canonicalPresentation.v1",
        role: "AGENCY",
        journeyStage: READY_COPY.journeyStage,
        condition: "READY",
        title: READY_COPY.title,
        reason: READY_COPY.reason,
        primaryAction: {
            intent: "OPEN_AGENCY_PROJECT_TASK",
            task: "REVIEW_BUSINESS_INFORMATION",
            projectId,
            label: READY_COPY.primaryLabel,
        },
        secondaryAction: null,
        waitingOn: null,
        blockers: [],
        progress: {
            current: 3,
            total: 7,
            label: READY_COPY.progressLabel,
        },
        expectedResult: READY_COPY.expectedResult,
        recovery: null,
        help: null,
        relevantRecords: [`${displayName} — client intake submitted`],
        advancedDetails: null,
    });
}
/**
 * First matching candidate in input order wins (deterministic; one Start Here).
 */
export function selectAgencyTodayStartHere(candidates) {
    for (const candidate of candidates) {
        const model = deriveAgencyTodayStartHere(candidate);
        if (model)
            return model;
    }
    return null;
}
/** Byte-stable serialization for determinism checks. */
export function canonicalizeAgencyTodayStartHere(model) {
    return stableStringify(model);
}
/** Point 3 / 5A seven-tier Agency Start Here priority. Lower rank wins. */
export const AGENCY_TODAY_START_HERE_TIERS = [
    "TIER_1_RECOVERY",
    "TIER_2_QUALITY_GATE",
    "TIER_3_CANDIDATE_REVIEW",
    "TIER_4_START_BUILD",
    "TIER_5_CHANGES_APPROVAL",
    "TIER_6_WAITING_CLIENT",
    "TIER_7_CARE_GROWTH",
];
function asStartHereInput(input) {
    return {
        projectId: input.projectId,
        businessName: input.businessName,
        clientDisplayName: input.clientDisplayName,
        clientReview: input.clientReview ?? null,
        lifecycle: input.lifecycle ?? null,
    };
}
function sevenTierDisplayName(input) {
    return resolveDisplayName(asStartHereInput(input));
}
export function classifyAgencyTodayStartHereTier(input) {
    if (input.qaFailure || input.lifecycle?.flags.qaFailure)
        return "TIER_1_RECOVERY";
    if (input.qualityGateNeedsReview)
        return "TIER_2_QUALITY_GATE";
    const candidate = deriveAgencyTodayStartHere(asStartHereInput(input));
    if (candidate)
        return "TIER_3_CANDIDATE_REVIEW";
    if (input.confirmedReadyToBuild)
        return "TIER_4_START_BUILD";
    if (input.changeRequestId || input.approvalNeeded)
        return "TIER_5_CHANGES_APPROVAL";
    if (input.waitingOnClient || input.clientReview?.clientLink?.status === "INTAKE_OPEN") {
        return "TIER_6_WAITING_CLIENT";
    }
    return "TIER_7_CARE_GROWTH";
}
function modelForTier(input, tier) {
    const displayName = sevenTierDisplayName(input);
    const projectId = input.projectId.trim();
    const inherited = deriveAgencyTodayStartHere(asStartHereInput({ ...input, projectId }));
    if (tier === "TIER_3_CANDIDATE_REVIEW" && inherited)
        return inherited;
    const copy = {
        TIER_1_RECOVERY: {
            condition: "NEEDS_ATTENTION",
            journeyStage: "Recover the build",
            title: "A build needs recovery",
            reason: "A failed, denied, or stale build is blocking this Delivery project.",
            primaryAction: {
                intent: "RETRY_PROJECT_BUILD",
                projectId,
                label: "Review recovery",
            },
            progressLabel: "Stage 5 of 7",
            current: 5,
            expectedResult: "The failed attempt is classified and a retry can start from exact input.",
        },
        TIER_2_QUALITY_GATE: {
            condition: "NEEDS_ATTENTION",
            journeyStage: "Inspect quality gate",
            title: "Review the quality gate",
            reason: "The latest build finished and needs a quality-gate inspection before preview.",
            primaryAction: {
                intent: "OPEN_QUALITY_GATE_REVIEW",
                projectId,
                label: "Open quality gate",
            },
            progressLabel: "Stage 5 of 7",
            current: 5,
            expectedResult: "Quality-gate truth is inspected on the exact run.",
        },
        TIER_3_CANDIDATE_REVIEW: {
            condition: "READY",
            journeyStage: READY_COPY.journeyStage,
            title: READY_COPY.title,
            reason: READY_COPY.reason,
            primaryAction: {
                intent: "OPEN_AGENCY_PROJECT_TASK",
                task: "REVIEW_BUSINESS_INFORMATION",
                projectId,
                label: READY_COPY.primaryLabel,
            },
            progressLabel: READY_COPY.progressLabel,
            current: 3,
            expectedResult: READY_COPY.expectedResult,
        },
        TIER_4_START_BUILD: {
            condition: "READY",
            journeyStage: "Start the website build",
            title: "Start the confirmed website build",
            reason: "Confirmed information is ready. Starting a build queues generation; it is not complete yet.",
            primaryAction: {
                intent: "START_PROJECT_BUILD",
                projectId,
                label: "Start build",
            },
            progressLabel: "Stage 4 of 7",
            current: 4,
            expectedResult: "A queued build is created from the confirmed candidate.",
        },
        TIER_5_CHANGES_APPROVAL: {
            condition: "READY",
            journeyStage: "Review changes or approval",
            title: "Review structured changes or exact approval",
            reason: "A change request or exact approval is waiting on this Delivery project.",
            primaryAction: input.changeRequestId
                ? {
                    intent: "OPEN_AGENCY_CHANGE_REQUEST_WORK",
                    projectId,
                    changeRequestId: input.changeRequestId,
                    label: "Review changes",
                }
                : {
                    intent: "OPEN_AGENCY_PROJECT_TASK",
                    task: "REVIEW_READINESS",
                    projectId,
                    label: "Review approval",
                },
            progressLabel: "Stage 6 of 7",
            current: 6,
            expectedResult: "Changes or exact approval are handled on the exact version.",
        },
        TIER_6_WAITING_CLIENT: {
            condition: "WAITING",
            journeyStage: "Waiting on the client",
            title: `Waiting on ${displayName}`,
            reason: "The next Delivery step is with the client. Agency work is waiting.",
            primaryAction: {
                intent: "OPEN_AGENCY_PROJECT_TASK",
                task: "OPEN_CLIENT_LINK",
                projectId,
                label: "Open client access",
            },
            progressLabel: "Stage 2 of 7",
            current: 2,
            expectedResult: "Client access stays copy-link only until the client continues.",
        },
        TIER_7_CARE_GROWTH: {
            condition: "COMPLETE",
            journeyStage: "You're clear",
            title: "You're clear on Delivery work",
            reason: "No Delivery recovery, quality-gate, review, build, or approval work is pending.",
            primaryAction: {
                intent: "OPEN_AGENCY_PROJECT_TASK",
                task: "REVIEW_READINESS",
                projectId,
                label: "Open work",
            },
            progressLabel: "Stage 7 of 7",
            current: 7,
            expectedResult: "Care or Growth work stays below Delivery priority.",
        },
    };
    const selected = copy[tier];
    return CanonicalPresentationModelV1Schema.parse({
        schemaId: "enrein.canonicalPresentation.v1",
        role: "AGENCY",
        journeyStage: selected.journeyStage,
        condition: selected.condition,
        title: selected.title,
        reason: selected.reason,
        primaryAction: selected.primaryAction,
        secondaryAction: null,
        waitingOn: selected.condition === "WAITING" ? "CLIENT" : null,
        blockers: [],
        progress: { current: selected.current, total: 7, label: selected.progressLabel },
        expectedResult: selected.expectedResult,
        recovery: tier === "TIER_1_RECOVERY" ? "Review the failed attempt before retrying." : null,
        help: null,
        relevantRecords: [`${displayName} — ${selected.journeyStage.toLowerCase()}`],
        advancedDetails: null,
    });
}
/**
 * Shared seven-tier compositor. UI must not rank.
 * Care/Growth (tier 7) never wins while any tier 1–5 candidate exists.
 */
export function composeAgencyTodayStartHere(candidates) {
    if (!candidates.length)
        return null;
    const ranked = candidates
        .map((candidate) => {
        const tier = classifyAgencyTodayStartHereTier(candidate);
        return { candidate, tier, rank: AGENCY_TODAY_START_HERE_TIERS.indexOf(tier) };
    })
        .sort((a, b) => a.rank - b.rank || 0);
    const winner = ranked[0];
    if (!winner)
        return null;
    const deliveryPending = ranked.some((row) => row.rank <= 4);
    if (winner.tier === "TIER_7_CARE_GROWTH" && deliveryPending) {
        const delivery = ranked.find((row) => row.rank <= 4);
        if (delivery) {
            return {
                tier: delivery.tier,
                model: modelForTier(delivery.candidate, delivery.tier),
            };
        }
    }
    return {
        tier: winner.tier,
        model: modelForTier(winner.candidate, winner.tier),
    };
}
//# sourceMappingURL=derive-agency-today-start-here.js.map